import { Injectable, NgZone } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export type UploadEntryState = 'queued' | 'uploading' | 'success' | 'error' | 'cancelled';

/** Eine Datei in der Übertragung — unabhängig vom Dialog, der sie ausgewählt hat. */
export interface UploadEntry {
  id: number;
  name: string;
  size: number;
  bucketId: string;
  state: UploadEntryState;
  /** 0–100, nur während der Übertragung aussagekräftig. */
  progress: number;
  error?: string;
}

/** Was das Kontingent gesprengt hat — Dateizahl oder Speicher. */
export type QuotaBreach = { kind: 'files' | 'size'; remaining: string } | null;

export interface UploadQueueState {
  entries: UploadEntry[];
  quotaBreach: QuotaBreach;
}

/** Ein Durchgang (eine Auswahl aus dem Dialog) ist durch. */
export interface UploadBatchDone {
  bucketId: string;
  succeeded: number;
  failed: number;
}

interface Batch {
  bucketId: string;
  uploader: FileUploader;
  items: Map<FileItem, UploadEntry>;
}

/**
 * Überträgt Dateien in die Wissensbasis portalweit im Hintergrund.
 *
 * Der Dialog wählt nur aus und übergibt (`enqueue`) — danach schließt er.
 * Die Übertragung läuft hier weiter, auch wenn der Nutzer die Seite wechselt;
 * den Stand zeigt `app-upload-tray` unten rechts. Was das Backend nach dem
 * Eingang tut (Speicher, Embedding), läuft ohnehin serverseitig in der Queue —
 * nur die Bytes selbst müssen vom Browser kommen. Deshalb warnt der Dienst
 * beim Schließen des Tabs, solange noch übertragen wird (`beforeunload`),
 * statt den Nutzer das Fenster offen halten zu lassen.
 *
 * Seiten mit Dateiliste hören auf `batchDone$` und laden ihre Liste neu,
 * wenn der Durchgang ihren Bucket betraf.
 */
@Injectable({ providedIn: 'root' })
export class FileUploadQueueService {
  private readonly batches: Batch[] = [];
  private nextId = 1;
  private quotaBreach: QuotaBreach = null;

  private readonly stateSubject = new BehaviorSubject<UploadQueueState>({
    entries: [],
    quotaBreach: null,
  });
  private readonly batchDoneSubject = new Subject<UploadBatchDone>();

  readonly state$: Observable<UploadQueueState> = this.stateSubject.asObservable();
  readonly batchDone$: Observable<UploadBatchDone> = this.batchDoneSubject.asObservable();

  private readonly onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.isUploading) return;
    event.preventDefault();
    // Ältere Browser zeigen die Rückfrage nur mit gesetztem returnValue.
    event.returnValue = '';
  };

  constructor(
    private authService: AuthService,
    private toastr: NbToastrService,
    private translate: TranslateService,
    private zone: NgZone,
  ) {
    window.addEventListener('beforeunload', this.onBeforeUnload);

    // Abmelden oder Mandantenwechsel: laufende Übertragungen gehören zur
    // alten Sitzung und würden mit fremdem Token weiterlaufen.
    this.authService.accessToken$
      .pipe(distinctUntilChanged())
      .subscribe((token) => {
        if (!token) {
          this.cancelAll();
          this.clear();
        }
      });
  }

  get isUploading(): boolean {
    return this.entries.some((e) => e.state === 'queued' || e.state === 'uploading');
  }

  private get entries(): UploadEntry[] {
    return this.batches.flatMap((b) => Array.from(b.items.values()));
  }

  /** Dateien übernehmen und sofort mit der Übertragung beginnen. */
  enqueue(bucketId: string, files: File[]): void {
    if (!files.length) return;

    const uploader = new FileUploader({
      url: environment.filesBackend + '/file/upload',
      method: 'POST',
      itemAlias: 'files',
      additionalParameter: { bucket_id: bucketId },
    });
    const batch: Batch = { bucketId, uploader, items: new Map() };

    uploader.addToQueue(files);
    for (const item of uploader.queue) {
      batch.items.set(item, {
        id: this.nextId++,
        name: item.file.name ?? '',
        size: item.file.size ?? 0,
        bucketId,
        state: 'queued',
        progress: 0,
      });
    }

    // Token je Datei frisch setzen: eine lange Übertragung überdauert
    // sonst den Token, mit dem sie begonnen hat.
    uploader.onBeforeUploadItem = (item) => {
      uploader.options.headers = [
        { name: 'Authorization', value: `Bearer ${this.authService.accessToken$.value}` },
      ];
      this.patch(batch, item, { state: 'uploading', progress: 0 });
    };
    uploader.onProgressItem = (item, progress) => {
      this.patch(batch, item, { progress: Number(progress) || 0 });
    };
    uploader.onSuccessItem = (item) => {
      this.patch(batch, item, { state: 'success', progress: 100 });
    };
    uploader.onErrorItem = (item, response, status) => {
      this.patch(batch, item, {
        state: 'error',
        error: this.describeUploadError(response, status),
      });
    };
    uploader.onCancelItem = (item) => {
      this.patch(batch, item, { state: 'cancelled' });
    };
    uploader.onCompleteAll = () => this.finishBatch(batch);

    this.quotaBreach = null;
    this.batches.push(batch);
    this.emit();
    uploader.uploadAll();
  }

  /** Bricht ab, was noch nicht übertragen ist; Fertiges bleibt. */
  cancelAll(): void {
    for (const batch of this.batches) {
      for (const [item, entry] of batch.items) {
        if (entry.state === 'queued') {
          // Wartende Dateien aus der Bibliotheks-Queue nehmen, sonst startet
          // sie die nächste, sobald die laufende abgebrochen ist.
          batch.uploader.removeFromQueue(item);
          entry.state = 'cancelled';
        }
      }
      batch.uploader.cancelAll();
    }
    this.emit();
  }

  /** Abgeschlossene Einträge aus der Anzeige nehmen. */
  clear(): void {
    for (let i = this.batches.length - 1; i >= 0; i--) {
      const done = Array.from(this.batches[i].items.values()).every(
        (e) => e.state !== 'queued' && e.state !== 'uploading',
      );
      if (done) this.batches.splice(i, 1);
    }
    if (!this.batches.length) this.quotaBreach = null;
    this.emit();
  }

  private finishBatch(batch: Batch): void {
    const entries = Array.from(batch.items.values());
    // Weggenommene (abgebrochene) Dateien meldet die Bibliothek nicht mehr.
    for (const entry of entries) {
      if (entry.state === 'queued' || entry.state === 'uploading') entry.state = 'cancelled';
    }
    const succeeded = entries.filter((e) => e.state === 'success').length;
    const failed = entries.filter((e) => e.state === 'error').length;
    this.emit();
    this.batchDoneSubject.next({ bucketId: batch.bucketId, succeeded, failed });

    if (this.isUploading) return;
    const all = this.entries;
    const ok = all.filter((e) => e.state === 'success').length;
    const bad = all.filter((e) => e.state === 'error').length;
    if (bad === 0 && ok > 0) {
      this.toastr.success(
        this.t('dialogs.fileUpload.tray.toastDone', { count: ok }),
        this.t('dialogs.fileUpload.tray.toastDoneTitle'),
      );
    } else if (bad > 0 && !this.quotaBreach) {
      this.toastr.danger(
        this.t('dialogs.fileUpload.summary.partial', { done: ok, failed: bad }),
        this.t('dialogs.fileUpload.failedTitle'),
      );
    }
  }

  private patch(batch: Batch, item: FileItem, changes: Partial<UploadEntry>): void {
    const entry = batch.items.get(item);
    if (!entry) return;
    Object.assign(entry, changes);
    this.emit();
  }

  private emit(): void {
    // Die XHR-Rückrufe kommen teils außerhalb der Zone an — die Anzeige
    // soll trotzdem sofort nachziehen.
    this.zone.run(() =>
      this.stateSubject.next({
        entries: this.entries.map((e) => ({ ...e })),
        quotaBreach: this.quotaBreach,
      }),
    );
  }

  // --- Fehlerdeutung ------------------------------------------------------

  /**
   * Backend-Fehler in einen Satz übersetzen, den der Kunde versteht.
   *
   * Zwei Gründe: die Rohmeldung ist englisch ("Quota exceeded: Only 0 files
   * remaining in the quota."), und bei einer Antwort, die kein JSON ist
   * (413 vom Ingress liefert HTML), hat `JSON.parse` vorher die Zeile
   * stillschweigend rot werden lassen, ohne je einen Grund zu zeigen.
   */
  private describeUploadError(response: string, status: number): string {
    const detail = this.extractDetail(response);

    if (status === 0) {
      return this.t('dialogs.fileUpload.errors.connection');
    }
    if (status === 413) {
      return this.t('dialogs.fileUpload.errors.tooLarge');
    }
    if (status === 401) {
      return this.t('dialogs.fileUpload.errors.unauthorized');
    }
    if (status === 403 && /quota exceeded/i.test(detail)) {
      const files = /only\s+(-?\d+)\s+files?\s+remaining/i.exec(detail);
      if (files) {
        const remaining = String(Math.max(0, Number(files[1])));
        this.quotaBreach = { kind: 'files', remaining };
        return this.t('dialogs.fileUpload.errors.quotaFiles', { remaining });
      }
      const bytes = /only\s+(-?\d+)\s+bytes?\s+remaining/i.exec(detail);
      if (bytes) {
        const remaining = (Math.max(0, Number(bytes[1])) / 1024 / 1024).toFixed(1);
        this.quotaBreach = { kind: 'size', remaining };
        return this.t('dialogs.fileUpload.errors.quotaSize', { remaining });
      }
      this.quotaBreach = { kind: 'files', remaining: '0' };
      return this.t('dialogs.fileUpload.errors.quota');
    }
    if (/unsupported file type/i.test(detail)) {
      return this.t('dialogs.fileUpload.errors.unsupportedType');
    }
    if (status >= 500) {
      return this.t('dialogs.fileUpload.errors.server');
    }
    return detail || this.t('dialogs.fileUpload.errors.generic');
  }

  /** Detailtext aus der Antwort holen, ohne an Nicht-JSON zu scheitern. */
  private extractDetail(response: string): string {
    if (!response) {
      return '';
    }
    try {
      const parsed = JSON.parse(response);
      const detail = parsed?.detail ?? parsed?.message;
      return typeof detail === 'string' ? detail : '';
    } catch {
      return '';
    }
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
